import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { FiSearch, FiTrendingUp } from 'react-icons/fi'
import { gigApi } from '@/services/api/gigs'
import { Gig } from '@/types/gig'
import LoadingSpinner from './LoadingSpinner'
import { CurrencyDisplay } from '@/components/currency'

interface HeroSearchBarProps {
    placeholder?: string
    className?: string
}

// Helper to get category name whether it's string or object
const getCategoryName = (category: any) => {
    if (!category) return 'Unknown'
    if (typeof category === 'string') {
        return category
    }
    return category.name || category.slug || 'Unknown'
}

const HeroSearchBar: React.FC<HeroSearchBarProps> = ({
    placeholder = "Search for gigs, skills, or services...",
    className = ""
}) => {
    const [searchQuery, setSearchQuery] = useState('')
    const [isOpen, setIsOpen] = useState(false)
    const [searchResults, setSearchResults] = useState<Gig[]>([])
    const [isLoading, setIsLoading] = useState(false)
    const [highlightedIndex, setHighlightedIndex] = useState(-1)

    const navigate = useNavigate()
    const dropdownRef = useRef<HTMLDivElement>(null)
    const inputRef = useRef<HTMLInputElement>(null)

    // Popular search terms
    const popularSearches = [
        'Delivery', 'Cleaning', 'Tutoring', 'Photography',
        'Writing', 'Design', 'Tech Support', 'Moving'
    ]

    // Debounced search
    useEffect(() => {
        if (!searchQuery.trim()) {
            setSearchResults([])
            setIsLoading(false)
            return
        }

        const timeoutId = setTimeout(async () => {
            setIsLoading(true)
            try {
                const response = await gigApi.searchGigs({
                    query: searchQuery,
                    limit: 6
                })
                // @ts-expect-error - Intentional nested data structure for API response
                setSearchResults(response.data.data.gigs || [])
            } catch (error) {
                console.error('Search failed:', error)
                setSearchResults([])
            } finally {
                setIsLoading(false)
            }
        }, 300)

        return () => clearTimeout(timeoutId)
    }, [searchQuery])

    // Handle click outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false)
                setHighlightedIndex(-1)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    const handleInputFocus = () => {
        setIsOpen(true)
        setHighlightedIndex(-1)
    }

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (!isOpen) {
            if (e.key === 'Enter' && searchQuery.trim()) {
                handleSearch(searchQuery)
            }
            return
        }

        const totalItems = searchQuery ? searchResults.length : popularSearches.length

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault()
                setHighlightedIndex(prev => (prev < totalItems - 1 ? prev + 1 : -1))
                break
            case 'ArrowUp':
                e.preventDefault()
                setHighlightedIndex(prev => (prev > -1 ? prev - 1 : totalItems - 1))
                break
            case 'Enter':
                e.preventDefault()
                if (highlightedIndex === -1) {
                    if (searchQuery.trim()) {
                        handleSearch(searchQuery)
                    }
                } else if (searchQuery && searchResults.length > 0) {
                    // Navigate to gig
                    if (highlightedIndex < searchResults.length) {
                        handleGigClick(searchResults[highlightedIndex]._id)
                    }
                } else if (!searchQuery && popularSearches.length > 0) {
                    // Use popular search
                    const selectedSearch = popularSearches[highlightedIndex]
                    setSearchQuery(selectedSearch)
                    handleSearch(selectedSearch)
                }
                break
            case 'Escape':
                setIsOpen(false)
                setHighlightedIndex(-1)
                inputRef.current?.blur()
                break
        }
    }

    const handleSearch = (query: string) => {
        if (!query.trim()) return
        navigate(`/gigs?search=${encodeURIComponent(query)}`)
        setIsOpen(false)
    }

    const handleGigClick = (gigId: string) => {
        navigate(`/gigs/${gigId}`)
        setIsOpen(false)
    }

    const handlePopularSearchClick = (searchTerm: string) => {
        setSearchQuery(searchTerm)
        handleSearch(searchTerm)
    }

    return (
        <div ref={dropdownRef} className={`relative ${className}`}>
            <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FiSearch className="h-5 w-5 text-gray-400" />
                </div>
                <input
                    ref={inputRef}
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onFocus={handleInputFocus}
                    onKeyDown={handleKeyDown}
                    className="block w-full pl-10 pr-3 py-4 border border-gray-300 dark:border-gray-600 rounded-lg leading-5 bg-white dark:bg-gray-700 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-lg font-inter shadow-sm"
                    placeholder={placeholder}
                />
            </div>

            {/* Dropdown */}
            {isOpen && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 z-50 max-h-96 overflow-y-auto">
                    {isLoading ? (
                        <div className="p-6 flex items-center justify-center">
                            <LoadingSpinner />
                        </div>
                    ) : searchQuery ? (
                        // Search results
                        <div className="py-2">
                            {searchResults && searchResults.length > 0 ? (
                                <>
                                    <div className="px-4 py-2 text-xs font-medium text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
                                        Search Results ({searchResults.length})
                                    </div>
                                    {searchResults.map((gig, index) => (
                                        <button
                                            key={gig._id}
                                            onClick={() => handleGigClick(gig._id)}
                                            className={`w-full px-4 py-4 text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${index === highlightedIndex ? 'bg-gray-50 dark:bg-gray-700' : ''
                                                }`}
                                        >
                                            <div className="font-medium text-gray-900 dark:text-white mb-1">
                                                {gig.title}
                                            </div>
                                            <div className="text-sm text-gray-500 dark:text-gray-400 mb-2 line-clamp-2">
                                                {gig.description}
                                            </div>
                                            <div className="flex items-center justify-between">
                                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800 dark:bg-primary-900 dark:text-primary-200 capitalize">
                                                    {getCategoryName(gig.category).replace('-', ' ')}
                                                </span>
                                                <div className="flex items-center gap-1 text-sm font-medium">
                                                    <CurrencyDisplay
                                                        amount={gig.payment.amount}
                                                        currency={gig.payment.currency}
                                                        compact={true}
                                                        variant="success"
                                                        className="text-green-600 dark:text-green-400"
                                                    />
                                                    {gig.payment.type === 'hourly' && <span>/hr</span>}
                                                </div>
                                            </div>
                                        </button>
                                    ))}
                                    <div className="border-t border-gray-100 dark:border-gray-700">
                                        <button
                                            onClick={() => handleSearch(searchQuery)}
                                            className="w-full px-4 py-3 text-left text-sm text-primary-600 dark:text-primary-400 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center transition-colors"
                                        >
                                            <FiSearch className="h-4 w-4 mr-2" />
                                            View all results for "{searchQuery}"
                                        </button>
                                    </div>
                                </>
                            ) : (
                                <div className="px-4 py-8 text-center">
                                    <FiSearch className="h-10 w-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                                    <p className="text-sm text-gray-500 dark:text-gray-400">
                                        No results found for "{searchQuery}"
                                    </p>
                                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                                        Try different keywords or check spelling
                                    </p>
                                </div>
                            )}
                        </div>
                    ) : (
                        // Popular searches when no input
                        <div className="py-2">
                            <div className="px-4 py-2 text-xs font-medium text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700 flex items-center">
                                <FiTrendingUp className="h-3 w-3 mr-1" />
                                Popular Searches
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 p-2">
                                {popularSearches.map((searchTerm, index) => (
                                    <button
                                        key={searchTerm}
                                        onClick={() => handlePopularSearchClick(searchTerm)}
                                        className={`px-3 py-2 text-left text-sm text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-700 rounded-md transition-colors ${index === highlightedIndex ? 'bg-gray-50 dark:bg-gray-700' : ''
                                            }`}
                                    >
                                        {searchTerm}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}

export default HeroSearchBar